import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Clock, ShieldCheck, Star } from 'lucide-react'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { siteConfig, QUOTE_PATH } from '@/constants/site'
import { reviewStats } from '@/constants/trust'

const trust = [
    { icon: Star, label: `${reviewStats.rating} average across ${reviewStats.count} reviews` },
    { icon: Clock, label: "Urgent visits average 2 h 10 min" },
    { icon: ShieldCheck, label: "Springs covered for life" }
]

export const HomeHero: FC = (): ReactNode => {
    return (
        <section id='hero' className='relative isolate flex min-h-[100svh] flex-col overflow-hidden bg-ink text-cream'>
            <div className='absolute inset-0 -z-10 animate-door-reveal' style={{ animationDelay: 'var(--enter-delay, 0ms)' }}>
                <div className='absolute inset-0 animate-hero-zoom' style={{ animationDelay: 'var(--enter-delay, 0ms)' }}>
                    <Image
                        src='/images/home/hero-garage-v2.png'
                        alt="Modern hillside home at sunset with a wood-look flush garage door, warm lighting and the city lights in the valley below"
                        fill
                        priority
                        sizes='100vw'
                        className='object-cover object-[72%_center] lg:object-[60%_center]'
                    />
                </div>

                <div className='absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/55 to-ink/10 lg:from-ink/80 lg:via-ink/30 lg:to-transparent' aria-hidden='true' />
                <div className='absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/50' aria-hidden='true' />
                <div className='bg-noise absolute inset-0 opacity-[0.06] mix-blend-overlay' aria-hidden='true' />
            </div>

            <Container className='flex flex-1 flex-col justify-between gap-14 pb-8 pt-40 md:pb-10 lg:pt-48'>
                <div className='max-w-3xl'>
                    <Enter delay={250}>
                        <Eyebrow tone='light'>Riverton metro · Garage door specialists since 2011</Eyebrow>
                    </Enter>

                    <HeroTitle
                        text="Your door, fixed today. Your price, fixed first."
                        accent={['today.', 'first.']}
                        accentClassName='text-ember-soft'
                        baseDelay={420}
                        className='mt-7 max-w-[13ch] text-display font-semibold text-cream'
                    />

                    <Enter delay={900} className='mt-8 max-w-lg text-lead text-pretty text-cream/75'>
                        Springs, openers, doors off their tracks. Every truck carries the parts to finish 9 in 10 repairs on the first visit, and your written price arrives before a single tool comes out.
                    </Enter>

                    <Enter delay={1050} className='mt-10 flex flex-wrap gap-3'>
                        <ButtonLink href={QUOTE_PATH} size='lg'>
                            Get my price in 60 seconds
                        </ButtonLink>

                        <ButtonLink href={siteConfig.phoneHref} variant='glass' size='lg' icon='phone'>
                            Call {siteConfig.phone}
                        </ButtonLink>
                    </Enter>
                </div>

                <Enter delay={1200}>
                    <div className='grid overflow-hidden rounded-[28px] border border-white/12 bg-ink/35 backdrop-blur-xl md:grid-cols-[1fr_1fr_1fr_1.15fr]'>
                        {trust.map(item => (
                            <div key={item.label} className='flex items-center gap-3 border-b border-white/10 px-5 py-4 text-[14.5px] text-cream/85 md:border-b-0 md:border-r md:px-6 md:py-6'>
                                <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-white/[0.08]'>
                                    <item.icon className='size-4 text-ember-soft' aria-hidden='true' />
                                </span>
                                {item.label}
                            </div>
                        ))}

                        <div className='hidden items-center justify-between gap-4 bg-white/[0.06] px-6 py-6 md:flex'>
                            <div>
                                <p className='flex items-center gap-2.5 font-mono text-[10.5px] uppercase tracking-[0.2em] text-cream/60'>
                                    <span className='relative flex size-2' aria-hidden='true'>
                                        <span className='absolute inset-0 rounded-full bg-moss-soft animate-live' />
                                        <span className='relative size-2 rounded-full bg-moss-soft' />
                                    </span>
                                    Recent job
                                </p>

                                <p className='mt-2 font-heading text-[1.15rem] font-semibold tracking-[-0.02em]'>
                                    Spring replacement
                                </p>
                            </div>

                            <p className='text-right text-[13.5px] text-cream/60'>
                                Oak Hollow
                                <span className='block font-mono text-cream'>48 min</span>
                            </p>
                        </div>
                    </div>
                </Enter>
            </Container>
        </section>
    )
}
